import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import NewsCard from '@/components/feature/NewsCard';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = (params.get('q') ?? '').trim();
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta({
    title: query ? `“${query}” 搜索结果 - 河马AV` : '搜索 - 河马AV',
    description: query
      ? `河马AV 关于“${query}”的影视作品与资讯搜索结果，支持影视标题与资讯关键词模糊搜索。`
      : '在河马AV搜索影视作品与行业资讯，支持关键词模糊匹配。',
    keywords: query ? `${query},影视搜索,河马AV` : '影视搜索,河马AV',
  });

  const { movieResults, newsResults } = useMemo(() => {
    if (!query) return { movieResults: [], newsResults: [] };
    const key = query.toLowerCase();
    const movieHits = movies.filter((movie) =>
      [movie.title, movie.originalTitle, movie.director, movie.region, ...movie.genres, ...movie.cast, movie.summary]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(key)),
    );
    const newsHits = news.filter((article) =>
      [article.title, article.summary, article.topic, article.content]
        .some((field) => field.toLowerCase().includes(key)),
    );
    return { movieResults: movieHits, newsResults: newsHits };
  }, [query]);

  const recommendedMovies = [...movies].sort((a, b) => b.score - a.score).slice(0, 5);
  const recommendedNews = news.slice(0, 3);
  const hasResults = movieResults.length > 0 || newsResults.length > 0;

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  }

  return (
    <Layout>
      <section className="border-b border-background-200 bg-primary-950">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <div className="text-background-50">
            <Breadcrumb items={[{ label: '搜索' }]} />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-3xl">
            {query ? `“${query}” 的搜索结果` : '站内搜索'}
          </h1>
          <form
            onSubmit={submitSearch}
            className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full bg-background-50 px-4 py-2.5"
          >
            <i className="ri-search-line text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入影视标题、类型或资讯关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-accent-500 px-4 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        {!query ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
              <i className="ri-search-2-line" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">开始搜索影视与资讯</h2>
            <p className="mt-2 text-sm text-foreground-500">在上方输入框输入关键词，即可查找影视作品与行业资讯。</p>
          </div>
        ) : hasResults ? (
          <div className="flex flex-col gap-12">
            <section>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="h-6 w-1.5 rounded-full bg-primary-500" />
                  影视作品
                </h2>
                <span className="text-sm text-foreground-500">
                  共 <span className="font-label font-bold text-primary-600">{movieResults.length}</span> 条
                </span>
              </div>
              {movieResults.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-5">
                  {movieResults.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-background-200 bg-background-50 px-5 py-8 text-center text-sm text-foreground-400">
                  没有匹配的影视作品
                </p>
              )}
            </section>

            <section>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                  资讯文章
                </h2>
                <span className="text-sm text-foreground-500">
                  共 <span className="font-label font-bold text-accent-600">{newsResults.length}</span> 条
                </span>
              </div>
              {newsResults.length > 0 ? (
                <div className="flex max-w-4xl flex-col gap-4">
                  {newsResults.map((article) => (
                    <NewsCard key={article.id} article={article} variant="list" />
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-background-200 bg-background-50 px-5 py-8 text-center text-sm text-foreground-400">
                  没有匹配的资讯文章
                </p>
              )}
            </section>
          </div>
        ) : (
          <div className="flex flex-col gap-10">
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-500">
                <i className="ri-emotion-sad-line" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">没有找到相关内容</h2>
              <p className="mt-2 text-sm text-foreground-500">
                “{query}” 暂无匹配结果，换个关键词试试，或看看下面的推荐内容。
              </p>
            </div>

            <section>
              <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-6 w-1.5 rounded-full bg-primary-500" />
                推荐影视
              </h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-5">
                {recommendedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                推荐资讯
              </h2>
              <div className="flex max-w-4xl flex-col gap-4">
                {recommendedNews.map((article) => (
                  <NewsCard key={article.id} article={article} variant="list" />
                ))}
              </div>
            </section>
          </div>
        )}

        {query && (
          <div className="mt-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-accent-600"
            >
              <i className="ri-home-4-line" />
              返回首页继续浏览
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}